// ABOUTME: Facts of the selected job attempt: failure reason, exit code, executor, timing, progress
// ABOUTME: and the resources it actually ran with. Its state is shown by its row in the attempt list.

import { Stack } from '@ds/coolshop';
import LinearProgress from '@mui/material/LinearProgress';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import { useAttemptReasonLabel } from '../../../registry';
import { JobAttempt } from '../../../types';
import { formatDuration } from '../../../utils/duration';
import { formatResources } from './formatResources';
import { InfoList } from './InfoList';

const formatDate = (value: string | null) =>
    value ? format(new Date(value), 'dd/MM/yyyy HH:mm:ss') : '';

export function AttemptSummary(props: Readonly<{ attempt: JobAttempt }>) {
    const { attempt } = props;
    const intl = useIntl();
    const reasonLabel = useAttemptReasonLabel();
    const running = attempt.state === 'running';

    const resources = formatResources(attempt.resources, {
        requests: intl.formatMessage({ id: 'job.resources.requests', defaultMessage: 'requests' }),
        limits: intl.formatMessage({ id: 'job.resources.limits', defaultMessage: 'limits' }),
    });

    return (
        <Stack gap={1}>
            <InfoList
                variant="grid"
                entries={[
                    {
                        key: 'reason',
                        label: intl.formatMessage({
                            id: 'job.attempt.reason',
                            defaultMessage: 'Reason',
                        }),
                        value: attempt.reason ? reasonLabel(attempt.reason) : '',
                    },
                    {
                        key: 'exitCode',
                        label: intl.formatMessage({
                            id: 'job.attempt.exitCode',
                            defaultMessage: 'Exit code',
                        }),
                        value: attempt.exitCode ?? '',
                    },
                    {
                        key: 'executor',
                        label: intl.formatMessage({
                            id: 'job.attempt.executor',
                            defaultMessage: 'Executor',
                        }),
                        value: attempt.executor,
                    },
                    {
                        key: 'notBefore',
                        wide: true,
                        label: intl.formatMessage({
                            id: 'job.attempt.notBefore',
                            defaultMessage: 'Not before',
                        }),
                        value: attempt.state === 'pending' ? formatDate(attempt.notBefore) : '',
                    },
                    {
                        key: 'startedAt',
                        wide: true,
                        label: intl.formatMessage({
                            id: 'job.started_at',
                            defaultMessage: 'Started At',
                        }),
                        value: formatDate(attempt.startedAt),
                    },
                    {
                        key: 'endedAt',
                        wide: true,
                        label: intl.formatMessage({
                            id: 'job.ended_at',
                            defaultMessage: 'Ended At',
                        }),
                        value: formatDate(attempt.endedAt),
                    },
                    {
                        key: 'duration',
                        label: intl.formatMessage({
                            id: 'job.duration',
                            defaultMessage: 'Duration',
                        }),
                        value: formatDuration(attempt.startedAt, attempt.endedAt),
                    },
                    {
                        key: 'resources',
                        wide: true,
                        label: intl.formatMessage({
                            id: 'job.resources.title',
                            defaultMessage: 'Resources',
                        }),
                        value: resources,
                    },
                ]}
            />
            {running && (
                <Stack gap={0.5}>
                    <LinearProgress variant="determinate" value={attempt.progress ?? 0} />
                    <InfoList
                        entries={[
                            {
                                key: 'progress',
                                label: intl.formatMessage({
                                    id: 'job.progress',
                                    defaultMessage: 'Progress',
                                }),
                                value: `${Math.round(attempt.progress ?? 0)}%${
                                    attempt.progressMessage ? ` — ${attempt.progressMessage}` : ''
                                }`,
                            },
                        ]}
                    />
                </Stack>
            )}
        </Stack>
    );
}
